import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { projectsApi } from '../../../api/projects';
import { useApp } from '../../../context/AppContext';
import ProjectTitleInput from '../../Common/ProjectTitleInput';
import { IMPORT_EXISTING_DESC } from '../../../utils/writerCopy';
import './CreateProjectModal.css';

const TYPE_OPTIONS = [
  { key: 'standalone', label: 'One story', desc: 'A novel, novella, or standalone work', isSeries: false },
  { key: 'series', label: 'A series', desc: 'Multiple connected books sharing a world or characters', isSeries: true },
  { key: 'unsure', label: 'Not sure yet', desc: 'Start exploring and figure it out as you go', isSeries: false },
];

const BEGIN_OPTIONS = [
  { key: 'fresh', label: 'Start fresh', desc: 'Open Discovery and write from here' },
  { key: 'import', label: 'Import existing work', desc: IMPORT_EXISTING_DESC },
];

const CreateProjectModal = ({ onClose, onCreated, onListed }) => {
  const app = useApp();
  const [name, setName] = useState('');
  const [selectedType, setSelectedType] = useState('standalone');
  const [beginDoor, setBeginDoor] = useState('fresh');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);
  const submittingRef = useRef(false);

  // autoFocus inside the portal does not always take focus.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el && document.activeElement !== el) {
        el.readOnly = false;
        el.focus();
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  const typeOption = TYPE_OPTIONS.find(t => t.key === selectedType);

  const finishCreated = (newProject, door, payload) => {
    const next = {};
    if (payload && typeof payload === 'object') {
      if (payload.openUpload) next.openUpload = true;
      if (payload.next) {
        next.next = payload.next;
        if (payload.session) next.session = payload.session;
        if (payload.story_id) next.story_id = payload.story_id;
        if (payload.batch_id) next.batch_id = payload.batch_id;
        if (payload.project_id) next.project_id = payload.project_id;
        if (payload.seriesProjectId) next.seriesProjectId = payload.seriesProjectId;
      }
    }
    onCreated(newProject, { door, ...next });
  };

  const handleCreate = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    // Read the DOM value: React state can lag the last keystrokes.
    const typed = (inputRef.current?.value ?? name).trim();
    try {
      setIsSubmitting(true);

      const newProject = await projectsApi.create({
        name: typed || 'Untitled project',
        medium: 'novel',
        is_series: typeOption.isSeries,
      });

      // The book exists now. The list needs the card before the writer
      // lands in the book, where the upload dialog opens.
      onListed?.(newProject);
      app?.noteCreatedProject?.(newProject);
      if (typeof app?.refreshProjects === 'function') void app.refreshProjects();

      if (beginDoor === 'import') {
        submittingRef.current = false;
        setIsSubmitting(false);
        finishCreated(newProject, 'import', { openUpload: true });
        return;
      }

      finishCreated(newProject, 'fresh');
    } catch (err) {
      console.error('Failed to create project:', err);
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return createPortal(
    <div className="create-project-modal__backdrop" onClick={onClose}>
      <div
        className="create-project-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="create-project-modal__header">
          <h2>Name your project</h2>
          <button className="create-project-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="create-project-modal__content">
          <p className="create-project-modal__subtitle">
            You can change this anytime. Brainstormy will help you figure out the rest.
          </p>

          <ProjectTitleInput
            ref={inputRef}
            className="create-project-modal__name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Untitled project..."
            autoFocus
            disabled={isSubmitting}
          />

          <div className="create-project-modal__type-selector">
            <p className="create-project-modal__type-label">What are you working on?</p>
            {TYPE_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`create-project-modal__type-option${selectedType === opt.key ? ' create-project-modal__type-option--selected' : ''}`}
                onClick={() => setSelectedType(opt.key)}
                disabled={isSubmitting}
                type="button"
              >
                <span className="create-project-modal__type-option-label">{opt.label}</span>
                <span className="create-project-modal__type-option-desc">{opt.desc}</span>
              </button>
            ))}
          </div>

          <div className="create-project-modal__type-selector">
            <p className="create-project-modal__type-label">How do you want to begin?</p>
            {BEGIN_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`create-project-modal__type-option${beginDoor === opt.key ? ' create-project-modal__type-option--selected' : ''}`}
                onClick={() => setBeginDoor(opt.key)}
                disabled={isSubmitting}
                type="button"
              >
                <span className="create-project-modal__type-option-label">{opt.label}</span>
                <span className="create-project-modal__type-option-desc">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="create-project-modal__actions">
          <button
            className="create-project-modal__submit"
            onClick={handleCreate}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Creating...'
              : beginDoor === 'import'
                ? 'Continue to upload \u2192'
                : 'Start writing \u2192'}
          </button>
          <button
            className="create-project-modal__cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateProjectModal;
